"use client";

import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import { useTranslations } from "next-intl";
import {
  ProfileStatIcon,
  type ProfileStatIconKey,
} from "@/lib/icons/profile-stats-icons";
import { formatProfileCount } from "@/lib/profile-utils";
import { cn } from "@/lib/utils";

function ProfileInlineSkeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("inline-block animate-pulse rounded-md bg-muted", className)}
    />
  );
}

type ProfileStatInlineProps<T extends ElementType = "span"> = {
  stat: ProfileStatIconKey;
  value: number;
  loading?: boolean;
  as?: T;
  iconClassName?: string;
  valueClassName?: string;
  className?: string;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "children">;

function statLabelKey(stat: ProfileStatIconKey, value: number): string {
  if (stat === "listings") return value === 1 ? "listingLabel" : "listingsLabel";
  if (stat === "sales") return value === 1 ? "saleLabel" : "salesLabel";
  if (stat === "followers") return value === 1 ? "followerLabel" : "followersLabel";
  return "followingLabel";
}

export function ProfileStatInline<T extends ElementType = "span">({
  stat,
  value,
  loading = false,
  as,
  iconClassName,
  valueClassName,
  className,
  ...rest
}: ProfileStatInlineProps<T>) {
  const t = useTranslations("profile");
  const Component = (as ?? "span") as ElementType;
  const label = t(statLabelKey(stat, value));

  return (
    <Component
      className={cn("inline-flex min-w-0 items-center gap-1", className)}
      aria-label={loading ? undefined : `${formatProfileCount(value)} ${label}`}
      title={loading ? undefined : `${formatProfileCount(value)} ${label}`}
      aria-busy={loading}
      {...rest}
    >
      <ProfileStatIcon
        name={stat}
        className={cn("h-3.5 w-3.5 shrink-0 text-muted-foreground", iconClassName)}
        aria-hidden
      />
      {loading ? (
        <ProfileInlineSkeleton className="h-3.5 w-7 rounded-full" />
      ) : (
        <span
          className={cn(
            "font-bold tabular-nums text-foreground",
            valueClassName,
          )}
        >
          {formatProfileCount(value)}
        </span>
      )}
    </Component>
  );
}

export function ProfileStatSkeleton({
  className,
  iconClassName,
}: {
  className?: string;
  iconClassName?: string;
}) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1", className)} aria-hidden>
      <ProfileInlineSkeleton className={cn("h-3.5 w-3.5 shrink-0 rounded-full", iconClassName)} />
      <ProfileInlineSkeleton className="h-3.5 w-7 rounded-full" />
    </span>
  );
}

export function ProfileRatingStatSkeleton({
  className,
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1", className)} aria-hidden>
      <ProfileInlineSkeleton
        className={cn(
          "shrink-0 rounded-full",
          compact ? "h-2.5 w-2.5" : "h-3 w-3",
        )}
      />
      <ProfileInlineSkeleton
        className={cn(
          "rounded-full",
          compact ? "h-3 w-6" : "h-3.5 w-8",
        )}
      />
    </span>
  );
}

export function ProfileStatSeparator({ className }: { className?: string }) {
  return (
    <span
      className={cn("px-0.5 font-semibold text-foreground/30", className)}
      aria-hidden
    >
      ·
    </span>
  );
}

export function ProfileStatRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mt-2 truncate text-[11px] font-medium tracking-[0.08px] text-foreground", className)}>
      {children}
    </div>
  );
}
